import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Slider } from '@mui/material';
import { saveAs } from 'file-saver';
import { useChowaTheme } from '../../config/chowaThemeContext';
import './calendar.css';
// import { buildTMSApiUrl } from '../config/tmsModels'; // 直接環境変数を使用

const TemperatureChart = ({ modelId, modelName }) => {
  const { isDark } = useChowaTheme();
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [zoomDomain, setZoomDomain] = useState([0, 24]);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [animationKey, setAnimationKey] = useState(0);
  const [isManualYAxis, setIsManualYAxis] = useState(false);
  const [manualYAxisMin, setManualYAxisMin] = useState('');
  const [manualYAxisMax, setManualYAxisMax] = useState('');

  // TMSでは常にデフォルトのグレー色を使用（シリーズ選択の影響を受けない）
  const defaultTheme = {
    primary: isDark ? '#6b7280' : '#4b5563', // グレー
    light: isDark ? '#374151' : '#f3f4f6',
    dark: isDark ? '#111827' : '#1f2937'
  };

  const fetchAvailableDates = useCallback(async (year, month) => {
    try {
      const months = [-1, 0, 1]; // 前月、当月、翌月
      const fetchPromises = months.map(offset => {
        const targetDate = new Date(year, month + offset, 1);
        const targetYear = targetDate.getFullYear();
        const targetMonth = targetDate.getMonth() + 1; // APIは1-12の月を期待
        
        // 動的API URL構築
        const apiUrl = `https://ksk-aircloud.com/api/chowa/tms/${modelId}/chart/available-date/?year=${targetYear}&month=${targetMonth}`;
        return fetch(apiUrl);
      });

      const responses = await Promise.all(fetchPromises);
      const jsonDataPromises = responses.map(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch available dates');
        }
        return response.json();
      });

      const jsonDataArray = await Promise.all(jsonDataPromises);

      const allAvailableDates = new Set();
      jsonDataArray.forEach(data => {
        if (data.available_dates && Array.isArray(data.available_dates)) {
          // YYYY-MM-DD形式の文字列をそのまま使用（タイムゾーンの問題を回避）
          data.available_dates.forEach(date => allAvailableDates.add(date));
        }
      });

      setAvailableDates(allAvailableDates);
    } catch (error) {
      console.error(`Error fetching available dates for ${modelName}:`, error);
    }
  }, [modelId, modelName]);

  useEffect(() => {
    fetchAvailableDates(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [currentMonth, fetchAvailableDates]);

  const fetchData = useCallback(async (date) => {
    try {
      const jstDateString = date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).replace(/\//g, '-');

      // 動的API URL構築
      const apiUrl = `https://ksk-aircloud.com/api/chowa/tms/${modelId}/chart/?date=${jstDateString}`;

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonData = await response.json();

      setData(jsonData);
      // データが更新された時にアニメーションを再実行
      setAnimationKey(prev => prev + 1);
    } catch (error) {
      console.error(`Error fetching data for ${modelName}:`, error);
      setData([]);
    }
  }, [modelId, modelName]);

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleMonthChange = (date) => {
    // カレンダーが表示している月の最初の日付を作成
    const newMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    setCurrentMonth(newMonth);
  };

  const handleZoomChange = (event, newValue) => {
    setZoomDomain(newValue);
  };

  const handleResetZoom = () => {
    setZoomDomain([0, 24]);
  };

  // 時間文字列（HH:MM:SS）を分に変換（0:00を0として）
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 分を時間文字列（HH:MM）に変換
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // X軸のフォーマッター（分を時間形式に変換）
  const formatXAxis = (minutes) => {
    return minutesToTime(minutes);
  };

  // データを時間（分）に変換して、ズーム範囲でフィルタリング
  const visibleData = data
    .map((item) => {
      const minutes = timeToMinutes(item.time);
      return {
        ...item,
        timeMinutes: minutes, // X軸用の数値
      };
    })
    .filter((item) => {
      const hour = parseInt(item.time.split(':')[0], 10);
      return hour >= zoomDomain[0] && hour < zoomDomain[1];
    });

  // X軸のdomain設定（ズーム範囲に基づいて分に変換）
  const getXAxisDomain = () => {
    const minMinutes = zoomDomain[0] * 60; // 時間を分に変換
    const maxMinutes = zoomDomain[1] * 60;
    return [minMinutes, maxMinutes];
  };

  // X軸の1時間ごとのメモリを生成
  const getXAxisTicks = () => {
    const ticks = [];
    const minHour = zoomDomain[0];
    let maxHour = zoomDomain[1];
    
    // 24:00は次の日の00:00なので、23:00まで表示
    if (maxHour === 24) {
      maxHour = 23;
    }
    
    // 1時間ごとにメモリを生成
    for (let hour = minHour; hour <= maxHour; hour++) {
      ticks.push(hour * 60); // 時間を分に変換
    }
    
    return ticks;
  };

  // Y軸のdomain設定（動的/手動切り替え）
  const getYAxisDomain = () => {
    if (isManualYAxis && manualYAxisMin !== '' && manualYAxisMax !== '') {
      return [parseFloat(manualYAxisMin), parseFloat(manualYAxisMax)];
    }
    return ['dataMin - 1', 'dataMax + 1'];
  };

  const handleDownloadCSV = () => {
    if (data.length === 0) {
      alert('ダウンロードするデータがありません。');
      return;
    }

    const csvHeader = 'Date,Time,Temperature\n';
    const csvData = data
      .map(item => `${item.date},${item.time},${item.temperature}`)
      .join('\n');
    
    const csvContent = csvHeader + csvData;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    const filename = `${modelId}_temperature_data_${selectedDate.toISOString().split('T')[0]}.csv`;
    saveAs(blob, filename);
  };

  return (
    <div className="space-y-4">
      {/* ヘッダー部分 */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white ml-32">
          {modelName} Temperature Chart
        </h2>
        
        <div className="flex items-end gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              日付選択:
            </label>
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              onMonthChange={handleMonthChange}
              dateFormat="yyyy/MM/dd"
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm lg:text-base
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent h-[42px]"
              dayClassName={(date) => {
                // YYYY-MM-DD形式に変換して比較（タイムゾーンの問題を回避）
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                const isAvailable = availableDates.has(dateString);
                
                // データがある場合はhas-dataクラスを付ける
                return isAvailable ? 'has-data' : '';
              }}
              popperClassName="react-datepicker-popper"
            />
          </div>
          
          <button
            onClick={handleDownloadCSV}
            className="px-4 py-2 text-sm lg:text-base bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 h-[42px]"
          >
            CSV ダウンロード
          </button>
        </div>
      </div>

      {/* チャート部分 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="h-64 lg:h-96">
          {visibleData.length > 0 ? (
            <ResponsiveContainer key={animationKey} width="100%" height="100%">
              <LineChart data={visibleData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                <XAxis
                  type="number"
                  dataKey="timeMinutes"
                  domain={getXAxisDomain()}
                  ticks={getXAxisTicks()}
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }}
                  stroke={isDark ? '#6b7280' : '#9ca3af'}
                />
                <YAxis
                  domain={getYAxisDomain()}
                  tick={{ fontSize: 12, fill: isDark ? '#9ca3af' : '#4b5563' }}
                  stroke={isDark ? '#6b7280' : '#9ca3af'}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            backgroundColor: isDark ? '#1f2937' : '#ffffff',
                            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: isDark ? '#f3f4f6' : '#111827'
                          }}
                        >
                          <p style={{ 
                            margin: '0 0 4px 0',
                            fontWeight: '600',
                            color: isDark ? '#e5e7eb' : '#374151'
                          }}>
                            時刻: {data.time}
                          </p>
                          <p style={{ margin: 0 }}>
                            温度: {payload[0].value}°C
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke={defaultTheme.primary}
                  strokeWidth={1.5}
                  dot={{ r: 2, fill: defaultTheme.primary }}
                  activeDot={{ r: 4, stroke: defaultTheme.primary, strokeWidth: 2, fill: defaultTheme.primary }}
                  isAnimationActive={true}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                選択された日付にデータがありません
              </p>
            </div>
          )}
        </div>

        {/* Y軸設定 */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300">
              Y軸設定:
            </span>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isManualYAxis}
                onChange={(e) => setIsManualYAxis(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm lg:text-base text-gray-700 dark:text-gray-300">
                手動設定
              </span>
            </label>
          </div>
          
          {isManualYAxis && (
            <div className="flex gap-4 mt-2">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  最小値:
                </label>
                <input
                  type="number"
                  value={manualYAxisMin}
                  onChange={(e) => setManualYAxisMin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="例: 0"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  最大値:
                </label>
                <input
                  type="number"
                  value={manualYAxisMax}
                  onChange={(e) => setManualYAxisMax(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="例: 100"
                />
              </div>
            </div>
          )}
        </div>

        {/* ズーム設定 */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300">
              ズーム範囲:
            </span>
            <button
              onClick={handleResetZoom}
              className="px-3 py-1 text-sm lg:text-base hover:bg-gray-600 text-white rounded transition-colors"
              style={{ backgroundColor: defaultTheme.primary }}
            >
              ズームをリセット
            </button>
          </div>
          <Slider
            value={zoomDomain}
            onChange={handleZoomChange}
            min={0}
            max={24}
            step={1}
            marks={[
              { value: 0, label: '0:00' },
              { value: 6, label: '6:00' },
              { value: 12, label: '12:00' },
              { value: 18, label: '18:00' },
              { value: 24, label: '24:00' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}:00`}
            sx={{
              '& .MuiSlider-rail': {
                backgroundColor: isDark ? '#374151' : '#e5e7eb',
              },
              '& .MuiSlider-track': {
                backgroundColor: defaultTheme.primary,
              },
              '& .MuiSlider-thumb': {
                backgroundColor: defaultTheme.primary,
              },
              '& .MuiSlider-mark': {
                backgroundColor: isDark ? '#4b5563' : '#9ca3af',
              },
              '& .MuiSlider-markLabel': {
                color: isDark ? '#9ca3af' : '#4b5563',
                fontSize: '0.75rem',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TemperatureChart;
